import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Star } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { IMAGES } from "../lib/images";
import { Reveal } from "../components/Reveal";
import { SEO } from "../components/SEO";
import { api } from "../lib/api";

const INSTAGRAM_IMAGES = [
  IMAGES.riviera, IMAGES.sunsetGlasses, IMAGES.bohoBeach,
  IMAGES.bohoHero, IMAGES.sunsetDinner, IMAGES.villefranche,
];

function SectionHead({ overline, title, center = false }) {
  return (
    <Reveal className={center ? "text-center" : ""}>
      <p className="overline-label">{overline}</p>
      <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-ink mt-4 leading-tight text-balance">{title}</h2>
      <div className={`w-16 h-px bg-gold mt-6 ${center ? "mx-auto" : ""}`} />
    </Reveal>
  );
}

export default function Home() {
  const { t, lang } = useLanguage();
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
  }, []);

  const cards = [
    {
      to: "/experiences/boho-picnic", img: IMAGES.bohoHero, tag: t("experiences.bohoTag"),
      name: "The Boho Picnic", price: `${t("experiences.from")} 59 € ${t("experiences.perPerson")}`,
      desc: t("experiences.bohoDesc"), testid: "experience-card-boho",
    },
    {
      to: "/experiences/floating-picnic", img: IMAGES.floatingHero, tag: t("experiences.floatingTag"),
      name: "Floating Picnic", price: `89 € ${t("experiences.perPerson")}`,
      desc: t("experiences.floatingDesc"), testid: "experience-card-floating",
    },
    {
      to: "/experiences/picnic-electronic", img: IMAGES.electronicHero, tag: t("experiences.electronicTag"),
      name: "Picnic Electronic", price: t("experiences.onRequest"),
      desc: t("experiences.electronicDesc"), testid: "experience-card-electronic",
    },
  ];

  return (
    <div data-testid="home-page">
      <SEO title="The Nice Picnic — Pique-niques d'exception sur la Côte d'Azur | Luxury Picnic Nice"
        description="Curated picnic experiences, secret spots and unforgettable moments between Nice and the Mediterranean. Boho picnic, floating picnic, sunset events & private celebrations." />

      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-end" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt="The Nice Picnic — guests sharing a sunset picnic on a hillside overlooking the Mediterranean"
            className="w-full h-full object-cover" fetchPriority="high" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#181513]/80 via-[#181513]/25 to-[#181513]/10" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-24 sm:pb-32 w-full">
          <Reveal>
            <p className="overline-label !text-gold">{t("hero.overline")}</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl text-ivory mt-5 max-w-3xl leading-[1.08] text-balance">
              {t("hero.title")}
            </h1>
            <p className="mt-6 max-w-xl text-ivory/85 text-base sm:text-lg font-light leading-relaxed">
              {t("hero.subtitle")}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/booking" data-testid="hero-book-btn" className="btn-primary !bg-terracotta hover:!bg-clay">
                {t("hero.ctaBook")} <ArrowRight size={16} />
              </Link>
              <a href="#experiences" data-testid="hero-discover-btn"
                className="btn-outline !border-ivory/50 !text-ivory hover:!border-ivory hover:!text-ivory">
                {t("hero.ctaDiscover")}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* BRAND INTRO */}
      <section className="py-24 sm:py-32" data-testid="intro-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16 items-center">
          <Reveal className="md:col-span-5 relative">
            <div className="img-frame organic-crop">
              <img src={IMAGES.foodWine} alt="The Nice Picnic setup with cushions, dreamcatcher and roses under an olive tree" className="w-full h-[420px] sm:h-[520px] object-cover" loading="lazy" />
            </div>
            <div className="hidden md:block absolute -bottom-10 -right-10 w-40 h-40 rounded-full border border-gold" aria-hidden="true" />
          </Reveal>
          <div className="md:col-span-6 md:col-start-7">
            <SectionHead overline={t("intro.overline")} title={t("intro.title")} />
            <Reveal delay={0.1}>
              <p className="mt-8 text-earth leading-relaxed text-base sm:text-lg font-light">{t("intro.p1")}</p>
              <p className="mt-5 text-earth leading-relaxed text-base sm:text-lg font-light">{t("intro.p2")}</p>
              <p className="mt-8 font-serif italic text-xl text-clay">{t("intro.signature")}</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* EXPERIENCES */}
      <section id="experiences" className="py-24 sm:py-32 bg-cream" data-testid="experiences-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead overline={t("experiences.overline")} title={t("experiences.title")} center />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
            {cards.map((c, i) => (
              <Reveal key={c.name} delay={i * 0.12}>
                <Link to={c.to} data-testid={c.testid} className="group block">
                  <div className={`img-frame ${i % 2 === 0 ? "organic-crop" : "organic-crop-alt"}`}>
                    <img src={c.img} alt={`${c.name} — picnic experience in Nice, French Riviera`} loading="lazy"
                      className="w-full h-72 sm:h-80 object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <p className="overline-label mt-7">{c.tag}</p>
                  <div className="flex items-baseline justify-between mt-2 gap-3">
                    <h3 className="font-serif text-2xl sm:text-3xl text-ink">{c.name}</h3>
                  </div>
                  <p className="text-sm text-clay tracking-wide mt-1">{c.price}</p>
                  <p className="text-earth text-sm leading-relaxed mt-4 font-light">{c.desc}</p>
                  <span className="inline-flex items-center gap-2 mt-5 text-xs uppercase tracking-[0.2em] text-ink group-hover:text-terracotta transition-colors">
                    {t("experiences.discover")} <ArrowRight size={14} />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FRENCH RIVIERA */}
      <section className="py-24 sm:py-32" data-testid="riviera-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="md:col-span-6 order-2 md:order-1">
            <SectionHead overline={t("riviera.overline")} title={t("riviera.title")} />
            <Reveal delay={0.1}>
              <p className="mt-8 text-earth leading-relaxed text-base sm:text-lg font-light">{t("riviera.p1")}</p>
              <p className="mt-5 font-serif italic text-xl text-clay">{t("riviera.p2")}</p>
            </Reveal>
          </div>
          <Reveal className="md:col-span-6 order-1 md:order-2 grid grid-cols-2 gap-4">
            <div className="img-frame organic-crop mt-10">
              <img src={IMAGES.villefranche} alt="Daytime picnic with woven poufs facing the bay of Saint-Jean-Cap-Ferrat" className="w-full h-64 sm:h-80 object-cover" loading="lazy" />
            </div>
            <div className="img-frame organic-crop-alt">
              <img src={IMAGES.riviera} alt="Golden light over an olive-tree picnic setup above the Mediterranean" className="w-full h-64 sm:h-80 object-cover" loading="lazy" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* LIFESTYLE */}
      <section className="py-24 sm:py-32 bg-burgundy text-ivory" data-testid="lifestyle-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl">
            <p className="overline-label !text-gold">{t("lifestyle.overline")}</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-4 leading-tight">{t("lifestyle.title")}</h2>
            <p className="mt-6 text-ivory/70 font-light leading-relaxed">{t("lifestyle.p")}</p>
          </Reveal>
          <div className="mt-14 grid grid-cols-2 md:grid-cols-12 gap-4 sm:gap-6">
            <Reveal className="col-span-2 md:col-span-7 img-frame organic-crop">
              <img src={IMAGES.sunsetDinner} alt="Friends sharing drinks on jute rugs at sunset, hills of the Riviera behind" className="w-full h-64 sm:h-96 object-cover" loading="lazy" />
            </Reveal>
            <Reveal delay={0.1} className="col-span-1 md:col-span-5 img-frame organic-crop-alt">
              <img src={IMAGES.foodBoard} alt="Champagne toast over a Nice Picnic table by the water" className="w-full h-48 sm:h-96 object-cover" loading="lazy" />
            </Reveal>
            <Reveal delay={0.15} className="col-span-1 md:col-span-5 img-frame organic-crop md:-mt-10">
              <img src={IMAGES.aperitif} alt="Picnic spread with red roses and The Nice Picnic chalkboard sign" className="w-full h-48 sm:h-72 object-cover" loading="lazy" />
            </Reveal>
            <Reveal delay={0.2} className="col-span-2 md:col-span-7 img-frame organic-crop-alt md:-mt-10">
              <img src={IMAGES.bohoBeach} alt="Guests gathered on cushions at dusk above the Mediterranean" className="w-full h-56 sm:h-72 object-cover" loading="lazy" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* PRIVATE EVENTS */}
      <section className="py-24 sm:py-32" data-testid="private-events-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <SectionHead overline={t("private.overline")} title={t("private.title")} />
            <Reveal delay={0.1}>
              <p className="mt-8 text-earth leading-relaxed font-light text-base sm:text-lg">{t("private.p")}</p>
              <div className="flex flex-wrap gap-3 mt-8">
                {t("private.categories").map((c) => (
                  <span key={c} className="text-xs tracking-[0.12em] uppercase border border-champagne rounded-full px-4 py-2 text-earth">{c}</span>
                ))}
              </div>
              <Link to="/private-events" data-testid="private-events-cta" className="btn-primary mt-10">
                {t("private.cta")} <ArrowRight size={16} />
              </Link>
            </Reveal>
          </div>
          <Reveal className="md:col-span-5 md:col-start-8">
            <div className="img-frame organic-crop">
              <img src={IMAGES.sunsetGlasses} alt="Romantic picnic for two under an olive tree — a proposal moment on the French Riviera" className="w-full h-[380px] sm:h-[460px] object-cover" loading="lazy" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 sm:py-32 bg-sand" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead overline={t("testimonials.overline")} title={t("testimonials.title")} center />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {testimonials.slice(0, 3).map((tm, i) => (
              <Reveal key={tm.id} delay={i * 0.12} className="border-t border-gold pt-8" >
                <div className="flex gap-1 text-terracotta" aria-label={`${tm.rating} stars`}>
                  {Array.from({ length: tm.rating }).map((_, s) => <Star key={s} size={14} fill="currentColor" />)}
                </div>
                <blockquote className="mt-5 font-serif text-xl leading-relaxed text-ink italic">
                  “{lang === "en" && tm.text_en ? tm.text_en : tm.text_fr}”
                </blockquote>
                <p className="mt-5 text-xs uppercase tracking-[0.2em] text-earth">{tm.name} — {tm.origin}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INSTAGRAM */}
      <section className="py-24 sm:py-32" data-testid="instagram-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
            <SectionHead overline={t("instagram.overline")} title={t("instagram.title")} />
            <a data-testid="instagram-follow-btn" href="https://www.instagram.com/the.nicepicnic" target="_blank" rel="noopener noreferrer"
              className="btn-outline whitespace-nowrap">{t("instagram.cta")}</a>
          </div>
          <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {INSTAGRAM_IMAGES.map((src, i) => (
              <a key={i} href="https://www.instagram.com/the.nicepicnic" target="_blank" rel="noopener noreferrer"
                data-testid={`instagram-tile-${i}`} className="group img-frame rounded-2xl aspect-square">
                <img src={src} alt="The Nice Picnic on Instagram — Riviera picnic moments" loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-28 sm:py-40" data-testid="final-cta-section">
        <div className="absolute inset-0">
          <img src={IMAGES.tropezSunset} alt="Sunset picnic with lanterns and friends overlooking the Mediterranean" className="w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-[#181513]/60" />
        </div>
        <Reveal className="relative z-10 text-center px-5">
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ivory text-balance">{t("finalCta.title")}</h2>
          <Link to="/booking" data-testid="final-book-btn" className="btn-primary mt-10 !px-12 !py-5">
            {t("finalCta.cta")} <ArrowRight size={16} />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
